<template>
  <div>
    <!-- Reflow Dependencies - Force this component's update hook to fire when these values change -->
    <!-- This is needed for smooth reflow. Otherwise, only BaseModal's update() hook will fire -->
    <span class="hidden reflow-depencencies">
      {{ reflowInc }}
    </span>
    <BaseModal @close="$emit('close')" cardWidth="560px" noPadding noToolbar noExit>
      <template #default>
        <!-- Header -->
        <div class="relative w-full flex gap-2 p-5 border-b border-border-normal">
          <component :is="platformIcon" class="text-icon-muted flex-shrink-0" />
          <div class="flex-grow flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Connect {{ formattedPlatformName }} Ad Account
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Select ad accounts to connect to this Lens brand.
            </BaseText>
          </div>
          <button class="absolute top-3 right-3 w-6 h-6 flex items-center justify-center"
          @click="$emit('close')">
            <TagRemoveIcon stroke="#5A667C" :width="16" :height="16" />
          </button>
        </div>
        <!-- Content -->
        <div ref="modalContent" class="w-full flex flex-col gap-3 px-5 pt-5 border-b border-border-normal">
          <AuthenticatePlatformButton 
            :platform="platform"
            :loading="loadingAuthURL"
            @click="handleAuthenticate"
          />
          <AdAccountMultiSelector 
            :ad-account-options="adAccountOptions"
            :selectedAccountIds="[...disabledAccountIds, ...selectedAccountIds]"
            :disabled-account-ids="disabledAccountIds"
            :loading="loadingAdAccounts"
            :max-list-height="222"
            @select="handleAccountSelect"
            @reflow="reflowInc++"
          />
        </div>
        <!-- Action Footer -->
        <div class="w-full flex items-center justify-between p-5">
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('close')">
            <BaseText type="label" size="sm" class="text-text-muted">
              Cancel
            </BaseText>
          </button>
          <button class="submit-btn flex justify-center px-3 py-1.5 rounded-md" :class="submitEnabled ? 'enabled' : 'disabled cursor-default'"
          :disabled="!submitEnabled" @click="handleSubmit" style="width: 170px;">
            <BaseLoadingSpinnerCircle v-if="loadingSubmit" :duration="1.2" class="text-text-disabled" />
            <BaseText v-else type="label" size="sm" class="transition-colors duration-100" :class="submitEnabled ? 'text-white' : 'text-text-disabled'">
              Connect Ad Accounts
            </BaseText>
          </button>
        </div>
        <!-- Authentication Controller Interface -->
        <LensPlatformAuthenticator 
          ref="platformAuthenticator"
          :loading="loadingAuthURL"
        />
      </template>
    </BaseModal>
  </div>
</template>

<script>
import smoothReflow from 'vue-smooth-reflow'

// Components
import AuthenticatePlatformButton from '../../lens/creation/drawer/AuthenticatePlatformButton.vue'
import AdAccountMultiSelector from '../../lens/AdAccountMultiSelector.vue'

// Controllers
import LensPlatformAuthenticator from '../../lens/LensPlatformAuthenticator.vue'

// Icons
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoTiktokIcon from '../../globals/Icons/socials/mono/MonoTiktokIcon.vue'

export default {
  name: 'ConnectAdAccountModal',
  mixins: [smoothReflow],
  components: {
    AuthenticatePlatformButton,
    AdAccountMultiSelector,
    LensPlatformAuthenticator,
    TagRemoveIcon,
  },
  props: {
    platform: {
      type: String,
      required: true
    },
    adAccountOptions: {
      type: Array,
      required: true
    },
    existingConnections: {
      type: Array,
      required: true
    },
    loadingAdAccounts: {
      type: Boolean,
      default: false
    },
    loadingSubmit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedAccountIds: [],
      loadingAuthURL: false,
      reflowInc: 0
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.modalContent,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.modalContent })
  },
  computed: {
    disabledAccountIds () {
      return this.existingConnections.map(connection => connection.id)
    },
    platformIcon () {
      return this.platform === 'meta' ? MonoMetaIcon : MonoTiktokIcon
    },
    formattedPlatformName () {
      return this.platform === 'meta' ? 'Meta' : 'TikTok'
    },
    submitEnabled () {
      return this.selectedAccountIds.length > 0 && !this.loadingSubmit
    }
  },
  methods: {
    handleAccountSelect (account) {
      if (this.disabledAccountIds.includes(account.id)) return
      if (this.selectedAccountIds.includes(account.id)) {
        this.selectedAccountIds = this.selectedAccountIds.filter(id => id !== account.id)
      } else {
        this.selectedAccountIds.push(account.id)
      }
    },
    async handleAuthenticate () {
      this.loadingAuthURL = true
      try {
        await this.$refs.platformAuthenticator.handlePlatformAuthenticate(this.platform)
        this.$emit('refresh-ad-accounts')
      } catch (error) {
        console.error('Failed to authenticate platform:', error)
      } finally {
        this.loadingAuthURL = false
      }
    },
    async handleSubmit () {
      this.$emit('connect-ad-accounts', this.selectedAccountIds)
    }
  }
}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}
</style>